import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';
import { ISchoolActiveStudentsItem } from '@/types';

const CHART_FONT_FAMILY = 'Aptos, sans-serif';

interface ISchoolActiveStudentsChartProps {
  data: ISchoolActiveStudentsItem[];
}

const SchoolActiveStudentsChart: React.FC<ISchoolActiveStudentsChartProps> = ({
  data,
}) => {
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';
  const { formatText } = useFormatText();

  const maxStudents = useMemo(
    () => Math?.max(...(data?.map((item) => item?.students) ?? [0]), 0),
    [data]
  );

  const yAxisMax = useMemo(() => {
    if (maxStudents === 0) return 10;
    const padded = Math?.ceil(maxStudents * 1.2);
    const magnitude = Math?.pow(10, Math?.floor(Math?.log10(padded)));
    const step = magnitude >= 10 ? magnitude / 2 : magnitude;
    return Math?.ceil(padded / step) * step;
  }, [maxStudents]);

  const series = useMemo(
    () => [
      {
        name: 'Students',
        data: data?.map((item) => item?.students) ?? [],
      },
    ],
    [data]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 220,
        toolbar: { show: false },
        fontFamily: CHART_FONT_FAMILY,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '65%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      fill: {
        opacity: 1,
        colors: ['#22C55E'],
      },
      colors: ['#22C55E'],
      xaxis: {
        categories: data?.map((item) => item?.name) ?? [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
          formatter: (value: string) =>
            value?.length > 15 ? `${value?.slice(0, 15)}…` : value,
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 4,
        opposite: isRTL,
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        position: 'back',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: { fontSize: '12px', fontFamily: CHART_FONT_FAMILY },
        x: { show: false },
        custom: ({ dataPointIndex }) => {
          const item = data[dataPointIndex];
          if (isRTL) {
            return `
              <div style="background:white;border:1px solid #f5f7fa;border-radius:5px;box-shadow:0 0 18px rgba(38,38,38,0.2);padding:10px 11px;min-width:110px;direction:rtl;text-align:right;">
                <div style="font-weight:600;font-size:12px;color:#22C55E;line-height:20px;margin-bottom:6px;font-family:Aptos,sans-serif;">${item?.name}</div>
                <div style="display:flex;justify-content:space-between;gap:8px;font-family:Aptos,sans-serif;">
                  <span style="font-size:12px;color:#525866;">${formatText('report-analytics.chart.students')}</span>
                  <span style="font-weight:600;font-size:12px;color:#181B25;">${item?.students}</span>
                </div>
              </div>`;
          }
          return `
            <div style="background:white;border:1px solid #f5f7fa;border-radius:5px;box-shadow:0 0 18px rgba(38,38,38,0.2);padding:10px 11px;min-width:110px;">
              <div style="font-weight:600;font-size:12px;color:#22C55E;line-height:20px;margin-bottom:6px;font-family:Aptos,sans-serif;">${item?.name}</div>
              <div style="display:flex;justify-content:space-between;gap:8px;font-family:Aptos,sans-serif;">
                <span style="font-size:12px;color:#525866;">${formatText('report-analytics.chart.students')}</span>
                <span style="font-weight:600;font-size:12px;color:#181B25;">${item?.students}</span>
              </div>
            </div>`;
        },
      },
      states: {
        hover: { filter: { type: 'darken', value: 0.85 } },
        active: { filter: { type: 'darken', value: 0.85 } },
      },
    }),
    [data, isRTL, formatText, yAxisMax]
  );

  return (
    <div className="w-full">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={220}
      />
    </div>
  );
};

export default SchoolActiveStudentsChart;
